import { useFieldContext } from '@/shared/lib/form';
import { useAdvisoryOptions, useAdvisoryOptionLabel } from '@/shared/utils';
import type { OptionItem } from '@/shared/utils';
import {
  Combobox,
  Field,
  Spinner,
} from '@noorden-insurtech/alba-design-system';
import { useEffect, useMemo, useState } from 'react';

interface AdvisoryComboboxFieldProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  pageSize?: number;
  helperText?: string;
  partyType?: 'advisory' | 'broker';
}

export function AdvisoryComboboxField({
  label,
  placeholder = 'Selecione a assessoria',
  required = false,
  disabled,
  pageSize = 1000,
  helperText,
  partyType,
}: AdvisoryComboboxFieldProps) {
  const field = useFieldContext<string>();
  const value = field.state.value;
  const [search, setSearch] = useState('');

  const { items, isLoading } = useAdvisoryOptions({
    search,
    pageSize,
    partyType,
    // Desabilitado o popup nunca abre: não vale buscar a lista inteira, basta
    // resolver o nome do item já selecionado.
    enabled: !disabled,
  });

  // Cache de `código → nome` que persiste entre buscas. Sem ele, ao selecionar
  // um item a busca muda (o Ark escreve o rótulo no input) e o item sai de
  // `items`, fazendo o trigger exibir só o código.
  const [labelByValue, setLabelByValue] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!items.length) return;
    setLabelByValue(prev => {
      let changed = false;
      const next = { ...prev };
      for (const item of items) {
        if (next[item.value] !== item.label) {
          next[item.value] = item.label;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  const cachedLabel = value ? labelByValue[value] : undefined;

  // Item selecionado fora da página atual da busca (produção tem mais
  // parceiros que o `pageSize`) ou pré-carregado na edição: resolve pelo código.
  const { label: resolvedLabel, isLoading: isResolvingLabel } =
    useAdvisoryOptionLabel({
      value: value && !cachedLabel ? value : undefined,
      partyType,
    });

  useEffect(() => {
    if (!value || !resolvedLabel) return;
    setLabelByValue(prev =>
      prev[value] === resolvedLabel ? prev : { ...prev, [value]: resolvedLabel }
    );
  }, [value, resolvedLabel]);

  const selectedLabel = cachedLabel ?? resolvedLabel;

  // A coleção do Combobox precisa conhecer o item selecionado mesmo quando a
  // busca atual não o devolve, senão o Ark perde o rótulo ao fechar o popup.
  const collection: OptionItem[] = useMemo(() => {
    if (!value || !selectedLabel || items.some(i => i.value === value)) {
      return items;
    }
    return [{ value, label: selectedLabel }, ...items];
  }, [items, value, selectedLabel]);

  const displayLabel = value
    ? (selectedLabel ??
      (isLoading || isResolvingLabel ? 'Carregando...' : value))
    : null;
  const showError =
    !field.state.meta.isValid &&
    (field.state.meta.isTouched || field.form.state.isSubmitted);

  return (
    <Field.Root invalid={showError} required={required} disabled={disabled}>
      <Field.Label className='text-alba-black-500'>{label}</Field.Label>
      <Combobox.Root
        items={collection}
        value={field.state.value ? [field.state.value] : []}
        onValueChange={details => field.handleChange(details.value[0] || '')}
        onInputValueChange={details => setSearch(details.inputValue)}
        selectionBehavior='replace'
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Trigger>{displayLabel ?? placeholder}</Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.Input
              placeholder={`Buscar ${partyType === 'advisory' ? 'assessoria' : 'corretora'}...`}
              autoFocus
            />
            {isLoading ? (
              <div className='py-8 flex items-center justify-center'>
                <Spinner size='sm' colorScheme='blue' />
              </div>
            ) : (
              <Combobox.List>
                <Combobox.ItemGroup>
                  <Combobox.ItemGroupLabel>
                    {partyType === 'advisory' ? 'Assessorias' : 'Corretoras'}
                  </Combobox.ItemGroupLabel>
                  {items.map(item => (
                    <Combobox.Item key={item.value} item={item}>
                      <Combobox.ItemText>{item.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
                </Combobox.ItemGroup>
                {search.trim().length > 0 && <Combobox.Empty />}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {helperText && <Field.HelperText>{helperText}</Field.HelperText>}
      {showError && (
        <Field.ErrorText>
          {field.state.meta.errors?.map(err => err.message).join(',')}
        </Field.ErrorText>
      )}
    </Field.Root>
  );
}
